import AnimatedSection from "@/components/AnimatedSection";

export interface AnswerQA {
  question: string;
  answer: string;
}

interface AnswerBlockProps {
  /** Short label above the block */
  label?: string;
  /** Direct, quotable answer to the page's core question (2 to 3 sentences max) */
  summary: string;
  /** Short factual bullets: prices, delays, zone, deliverables */
  facts?: string[];
  /** Visible question and answer pairs, used by answer engines */
  questions?: AnswerQA[];
}

/**
 * Answer-first summary block (AEO/GEO): gives assistants and answer engines
 * a short, self-contained, quotable answer at the top of the page.
 */
const AnswerBlock = ({ label = "En bref", summary, facts, questions }: AnswerBlockProps) => (
  <AnimatedSection>
    <section
      aria-label={label}
      className="container max-w-3xl"
    >
      <div className="rounded-xl border border-border bg-secondary/40 p-6 md:p-8">
        <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-4">
          {label}
        </p>
        <p className="font-body text-base md:text-lg leading-relaxed text-foreground whitespace-pre-line">
          {summary}
        </p>

        {facts && facts.length > 0 && (
          <ul className="mt-6 grid gap-2 sm:grid-cols-2">
            {facts.map((fact) => (
              <li
                key={fact}
                className="font-body text-sm text-muted-foreground leading-relaxed pl-4 relative before:absolute before:left-0 before:top-[0.6em] before:h-1 before:w-1 before:rounded-full before:bg-primary"
              >
                {fact}
              </li>
            ))}
          </ul>
        )}

        {questions && questions.length > 0 && (
          <dl className="mt-8 space-y-5">
            {questions.map((qa) => (
              <div key={qa.question}>
                <dt className="font-heading text-base md:text-lg font-normal text-foreground">
                  {qa.question}
                </dt>
                <dd className="mt-1 font-body text-sm md:text-base text-muted-foreground leading-relaxed whitespace-pre-line">
                  {qa.answer}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  </AnimatedSection>
);

export default AnswerBlock;
